import React, { createContext, useContext, useMemo, useReducer, useEffect } from 'react';
import { buildTheme } from '../theme/theme';
import { STORAGE_KEYS, readStorage, writeStorage } from '../utils/storage';

const defaultItinerary = {
  days: [{ date: new Date().toISOString().slice(0, 10), stops: [] }],
};

const initialState = {
  savedPlaces: readStorage(STORAGE_KEYS.savedPlaces, []),
  placeCatalog: readStorage(STORAGE_KEYS.placeCatalog, {}),
  comparePlaces: [],
  itinerary: readStorage(STORAGE_KEYS.itinerary, defaultItinerary),
};

const TravelGuideContext = createContext(null);

const reducer = (state, action) => {
  switch (action.type) {
    case 'UPSERT_CATALOG': {
      const nextCatalog = { ...state.placeCatalog };
      action.payload.forEach((place) => {
        if (place?.id) nextCatalog[place.id] = place;
      });
      return { ...state, placeCatalog: nextCatalog };
    }
    case 'TOGGLE_SAVE': {
      const place = action.payload;
      if (!place?.id) return state;
      const exists = state.savedPlaces.includes(place.id);
      const savedPlaces = exists
        ? state.savedPlaces.filter((id) => id !== place.id)
        : [...state.savedPlaces, place.id];
      const placeCatalog = { ...state.placeCatalog, [place.id]: place };
      return { ...state, savedPlaces, placeCatalog };
    }
    case 'TOGGLE_COMPARE': {
      const placeId = action.payload;
      const exists = state.comparePlaces.includes(placeId);
      if (exists) {
        return { ...state, comparePlaces: state.comparePlaces.filter((id) => id !== placeId) };
      }
      if (state.comparePlaces.length >= 3) return state;
      return { ...state, comparePlaces: [...state.comparePlaces, placeId] };
    }
    case 'CLEAR_COMPARE':
      return { ...state, comparePlaces: [] };
    case 'SET_ITINERARY':
      return { ...state, itinerary: action.payload };
    case 'ADD_DAY':
      return {
        ...state,
        itinerary: {
          days: [...state.itinerary.days, { date: action.payload, stops: [] }],
        },
      };
    case 'ADD_STOP': {
      const { dayIndex, placeId } = action.payload;
      const days = state.itinerary.days.map((day, idx) => {
        if (idx !== dayIndex) return day;
        if (day.stops.includes(placeId)) return day;
        return { ...day, stops: [...day.stops, placeId] };
      });
      return { ...state, itinerary: { days } };
    }
    case 'REMOVE_STOP': {
      const { dayIndex, placeId } = action.payload;
      const days = state.itinerary.days.map((day, idx) => (
        idx === dayIndex ? { ...day, stops: day.stops.filter((id) => id !== placeId) } : day
      ));
      return { ...state, itinerary: { days } };
    }
    case 'MOVE_STOP': {
      const { dayIndex, fromIndex, toIndex } = action.payload;
      const days = state.itinerary.days.map((day, idx) => {
        if (idx !== dayIndex) return day;
        if (fromIndex < 0 || toIndex < 0 || fromIndex >= day.stops.length || toIndex >= day.stops.length) return day;
        const stops = [...day.stops];
        const [moved] = stops.splice(fromIndex, 1);
        stops.splice(toIndex, 0, moved);
        return { ...day, stops };
      });
      return { ...state, itinerary: { days } };
    }
    default:
      return state;
  }
};

export const TravelGuideProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.savedPlaces, state.savedPlaces);
  }, [state.savedPlaces]);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.placeCatalog, state.placeCatalog);
  }, [state.placeCatalog]);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.itinerary, state.itinerary);
  }, [state.itinerary]);

  const value = useMemo(() => {
    const theme = buildTheme('light');
    const savedPlaceDetails = state.savedPlaces
      .map((id) => state.placeCatalog[id])
      .filter(Boolean);
    const comparedPlaceDetails = state.comparePlaces
      .map((id) => state.placeCatalog[id])
      .filter(Boolean);

    return {
      state,
      theme,
      savedPlaceDetails,
      comparedPlaceDetails,
      dispatch,
    };
  }, [state]);

  return <TravelGuideContext.Provider value={value}>{children}</TravelGuideContext.Provider>;
};

export const useTravelGuide = () => {
  const value = useContext(TravelGuideContext);
  if (!value) throw new Error('useTravelGuide must be used inside TravelGuideProvider');
  return value;
};
